import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageTemplate from '../components/Common/PageTemplate';

const DynamicPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchPage = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`http://localhost:8000/api/v1/page/${slug}/`);
            
            if (!response.ok) {
                if (response.status === 404) {
                    setError('Sayfa bulunamadı');
                    return;
                }
                throw new Error('Sayfa yüklenirken hata oluştu');
            }

            const data = await response.json();
            setPage(data);
        } catch (err) {
            console.error('Sayfa yükleme hatası:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        fetchPage();
    }, [fetchPage]);

    // Dinamik CSS ekleme
    const addStyles = useCallback(() => {
        if (page?.css_content) {
            // Mevcut sayfa stil'ini kaldır
            const existingStyle = document.getElementById('dynamic-page-styles');
            if (existingStyle) {
                existingStyle.remove();
            }
            
            // Yeni stil ekle
            const style = document.createElement('style');
            style.id = 'dynamic-page-styles';
            style.textContent = page.css_content;
            document.head.appendChild(style);
        }
    }, [page?.css_content]);

    // Dinamik JavaScript ekleme
    const addScripts = useCallback(() => {
        if (page?.javascript_content) {
            // Mevcut sayfa script'ini kaldır
            const existingScript = document.getElementById('dynamic-page-scripts');
            if (existingScript) {
                existingScript.remove();
            }
            
            // Yeni script ekle
            const script = document.createElement('script');
            script.id = 'dynamic-page-scripts';
            script.textContent = page.javascript_content;
            document.body.appendChild(script);
        }
    }, [page?.javascript_content]);

    // Sayfa yüklendiğinde stil ve script'leri ekle
    useEffect(() => {
        if (page) {
            addStyles();
            addScripts();
            
            // Sayfa title'ını güncelle
            if (page.meta_title) {
                document.title = page.meta_title;
            } else {
                document.title = page.title;
            }
            
            // Meta description güncelle
            if (page.meta_description) {
                let metaDescription = document.querySelector('meta[name="description"]');
                if (!metaDescription) {
                    metaDescription = document.createElement('meta');
                    metaDescription.name = 'description';
                    document.head.appendChild(metaDescription);
                }
                metaDescription.content = page.meta_description;
            }
        }
        
        // Component unmount olduğunda temizlik yap
        return () => {
            const style = document.getElementById('dynamic-page-styles');
            if (style) style.remove();
            
            const script = document.getElementById('dynamic-page-scripts');
            if (script) script.remove();
            
            // Title'ı varsayılana çevir
            document.title = 'Hacettepe Üniversitesi Fen Fakültesi';
        };
    }, [page, addStyles, addScripts]);

    if (loading) {
        return (
            <PageTemplate 
                title="Yükleniyor..."
                breadcrumbs={[
                    { label: 'Ana Sayfa', href: '/' },
                    { label: 'Yükleniyor...' }
                ]}
            >
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </PageTemplate>
        );
    }

    if (error) {
        return (
            <PageTemplate 
                title="Hata"
                breadcrumbs={[
                    { label: 'Ana Sayfa', href: '/' },
                    { label: 'Hata' }
                ]}
            >
                <div className="text-center py-20">
                    <div className="text-red-600 text-6xl mb-4">⚠️</div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">{error}</h2>
                    <button 
                        onClick={() => navigate('/')}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Ana Sayfaya Dön
                    </button>
                </div>
            </PageTemplate>
        );
    }

    if (!page) {
        return null;
    }

    const breadcrumbs = [
        { label: 'Ana Sayfa', href: '/' },
        { label: page.title }
    ];

    return (
        <PageTemplate 
            title={page.title}
            breadcrumbs={breadcrumbs}
        >
            <div className="dynamic-page-content">
                {/* HTML içeriği güvenli şekilde render et */}
                <div 
                    dangerouslySetInnerHTML={{ __html: page.html_content }}
                />
            </div>
        </PageTemplate>
    );
};

export default DynamicPage; 